import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import { UUID } from 'crypto';
import { requireApp } from 'store/assertions';

const getApiClient = () => requireApp().apiClient;
const getGridId = () => requireApp().gridId;

const buildExportedFilesPath = (suffix = ''): string => {
    const gridId = getGridId();
    return `grids/${gridId}/exported-files${suffix}`;
};

export type ExportedFileType =
    | 'SCREENSHOT'
    | 'ANIMATED_360_ORBIT'
    | 'ANIMATED_360_LOOK_AROUND'
    | 'ANIMATED_TIMELAPSE'
    | 'ANIMATED_SCREEN_RECORDING'
    | '3D_3DM'
    | '3D_OBJ'
    | '3D_STLF'
    | '3D_GLTF'
    | '3D_DXF'
    | 'CSV_DATA'
    | 'TSV_DATA'
    | 'JSON_DATA';

export interface ExportedFileSummary {
    id: UUID;
    name: string;
    type: ExportedFileType;
    createdAt: string;
}

export interface ExportedFileDetail extends ExportedFileSummary {
    contentType: string | null;
    size: number | null;
    downloadUrl?: string;
}

export interface CreateExportedFilePayload {
    name: string;
    type: ExportedFileType;
}

export interface UpdateExportedFilePayload {
    name?: string;
    type?: ExportedFileType;
}

export interface CreateExportedFileResponse extends ExportedFileDetail {
    uploadUrl: string;
}

export type ExportedFileId = UUID;

export async function listExportedFiles(): Promise<ExportedFileSummary[]> {
    const response: AxiosResponse<ExportedFileSummary[]> = await getApiClient().get(buildExportedFilesPath());
    return response.data;
}

export async function getExportedFile(exportedFileId: ExportedFileId): Promise<ExportedFileDetail> {
    const response: AxiosResponse<ExportedFileDetail> = await getApiClient().get(buildExportedFilesPath(`/${exportedFileId}`));
    return response.data;
}

export async function createExportedFile(payload: CreateExportedFilePayload): Promise<CreateExportedFileResponse> {
    const response: AxiosResponse<CreateExportedFileResponse> = await getApiClient().post(buildExportedFilesPath(), payload);
    return response.data;
}

export async function updateExportedFile(exportedFileId: ExportedFileId, payload: UpdateExportedFilePayload): Promise<ExportedFileDetail> {
    const response: AxiosResponse<ExportedFileDetail> = await getApiClient().patch(buildExportedFilesPath(`/${exportedFileId}`), payload);
    return response.data;
}

export async function deleteExportedFile(exportedFileId: ExportedFileId): Promise<void> {
    await getApiClient().delete(buildExportedFilesPath(`/${exportedFileId}`));
}

export async function completeExportedFileUpload(exportedFileId: ExportedFileId): Promise<ExportedFileDetail> {
    const response: AxiosResponse<ExportedFileDetail> = await getApiClient().post(buildExportedFilesPath(`/${exportedFileId}/complete`));
    return response.data;
}

export async function createAndUploadExportedFile(payload: CreateExportedFilePayload, data: Blob): Promise<ExportedFileDetail> {
    const uploadConfig: AxiosRequestConfig = {
        headers: {
            'Content-Type': data.type || 'application/octet-stream',
        },
    };
    const createdFile = await createExportedFile(payload);
    await axios.put(createdFile.uploadUrl, data, uploadConfig);
    return completeExportedFileUpload(createdFile.id);
}
